import { PlusSquareIcon, SparkleIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FieldValues, Path } from "react-hook-form";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useAssets } from "../hooks/useAssets";
import type { ParameterAssetSelection } from "../lib/parameter-asset";
import { CLEARED_PARAMETER_ASSET } from "../lib/parameter-asset";
import {
  formatParameterLabel,
  isColorParameter,
  isHexColor,
} from "../lib/parameter-label";
import { validateNotBlanked } from "../lib/parameter-panel-helpers";
import { ParameterAssetModal } from "./ParameterAssetModal";

interface ParameterInputProps {
  paramKey: string;
  displayName?: string;
  disabled?: boolean;
  onChange?: () => void;
  isModified?: boolean;
  /** True when the value is auto-managed (image-extracted, no asset link,
   *  never user-set) — shows the Auto badge and dimmed value text. */
  isAutoValue?: boolean;
  groupName?: string;
  /** Project of the refined image; calls out the project's own assets in the
   *  modal's Assets tab. */
  projectId?: string | null;
  /** True while this param's value is being re-read from the new render. */
  isResyncing?: boolean;
  /** Original image value; blanking a previously-set value fails validation. */
  originalValue?: string;
}

export function autoResize(element: HTMLTextAreaElement) {
  // Collapse first so a flex-stretched box can't inflate scrollHeight.
  element.style.height = "0px";
  element.style.height = `${element.scrollHeight}px`;
}

function useParameterInputRefs(onChange: (() => void) | undefined) {
  const onChangeRef = useRef(onChange);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    autoResize(el);

    // jsdom (and older browsers) have no ResizeObserver — the one-shot
    // measure above is enough there. In the app, the refine drawer animates
    // width on expand; measuring during that animation wraps the value and
    // locks in a huge height. Re-run when width changes, ignoring
    // height-only notifications from autoResize.
    if (typeof ResizeObserver === "undefined") return;

    let lastWidth = el.getBoundingClientRect().width;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width === lastWidth) return;
      lastWidth = width;
      autoResize(el);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { onChangeRef, textareaRef };
}

function buildContainerClasses(
  disabled: boolean,
  isModified: boolean,
  hasError: boolean,
): string {
  const base =
    "group relative flex items-center justify-between gap-3 px-3 py-2 bg-[var(--bg-field)] border rounded-lg transition-all duration-200";
  if (hasError) {
    return `${base} border-[var(--stroke-destructive)] focus-within:ring-2 focus-within:ring-[var(--stroke-destructive)]/20`;
  }
  const stateClass = disabled
    ? "opacity-60 cursor-not-allowed border-transparent"
    : "border-transparent";
  const modifiedClass = isModified
    ? "border-[var(--accent-secondary-hover)]"
    : "";
  return `${base} ${stateClass} ${modifiedClass}`;
}

interface ParameterIndicatorsProps {
  isAutoValue: boolean;
  isModified: boolean;
  isResyncing?: boolean;
}

function ParameterIndicators({
  isAutoValue,
  isModified,
  isResyncing,
}: ParameterIndicatorsProps) {
  if (isResyncing) {
    return (
      <div
        role="status"
        className="flex items-center gap-1 shrink-0 text-xs font-medium text-[var(--text-secondary)] animate-pulse"
        title="Updating from the new image"
      >
        {/* Visually hidden on small screens but always read by assistive tech. */}
        <span className="sr-only sm:not-sr-only">Syncing</span>
        <SparkleIcon className="w-3.5 h-3.5" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 shrink-0">
      {isAutoValue && !isModified && (
        <div className="flex items-center gap-1 text-xs font-medium text-[var(--text-secondary)] parameter-textarea group-focus-within:text-[var(--accent-primary)] transition-colors">
          <span className="hidden sm:inline">Auto</span>
          <SparkleIcon className="w-3.5 h-3.5" />
        </div>
      )}
      {isModified && (
        <div
          className="w-1.5 h-1.5 bg-[var(--accent-secondary-hover)] rounded-full"
          title="Modified"
        />
      )}
    </div>
  );
}

interface ColorSwatchProps {
  paramKey: string;
}

function ColorSwatch({ paramKey }: ColorSwatchProps) {
  const { control } = useFormContext<FieldValues>();
  const hex = useWatch({
    control,
    name: paramKey as Path<FieldValues>,
  }) as string | undefined;
  const isValid = hex ? isHexColor(hex) : false;
  return (
    <div
      className="w-4 h-4 rounded shrink-0 border border-[var(--stroke-field)]"
      style={{ backgroundColor: isValid ? hex : "transparent" }}
      aria-hidden="true"
    />
  );
}

interface AssetTriggerProps {
  disabled: boolean;
  onClick: () => void;
}

function AssetTrigger({ disabled, onClick }: AssetTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Add asset"
      className="text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <PlusSquareIcon className="h-4 w-4" />
    </button>
  );
}

interface AssetValueChipProps {
  paramKey: string;
  displayName: string;
  assetIdKey: Path<FieldValues>;
  assetUrlKey: Path<FieldValues>;
  name: string;
  disabled: boolean;
  onRemove: () => void;
}

/** Read-only display for a value that came from an asset or an upload: the
 *  color tile or image thumbnail plus its name (the prompt still uses the
 *  value). To change it, reopen the modal — or clear it with the × . */
function AssetValueChip({
  paramKey,
  displayName,
  assetIdKey,
  assetUrlKey,
  name,
  disabled,
  onRemove,
}: AssetValueChipProps) {
  const { control } = useFormContext<FieldValues>();
  const value = useWatch({ control, name: paramKey as Path<FieldValues> }) as
    | string
    | undefined;
  const assetId = useWatch({ control, name: assetIdKey }) as
    | string
    | null
    | undefined;
  const assetUrl = useWatch({ control, name: assetUrlKey }) as
    | string
    | null
    | undefined;

  const isColor = isColorParameter({ value: value ?? "", key: displayName });
  // An unsaved upload carries its own URL; a saved image's is looked up from
  // the asset library rather than cached into the form at commit time — the
  // library owns the URL's lifecycle, and the query is cache-shared with the
  // Assets tab, so after the first load the lookup costs nothing.
  const needsLookup = !(isColor || assetUrl) && Boolean(assetId);
  const { assets } = useAssets(needsLookup);
  const imageUrl =
    assetUrl ?? assets.find((asset) => asset.id === assetId)?.value;

  return (
    <div className="flex flex-1 min-w-0 items-center gap-2">
      {isColor ? (
        <ColorSwatch paramKey={paramKey} />
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-4 w-4 shrink-0 rounded object-cover"
        />
      ) : (
        <span
          aria-hidden="true"
          className="h-4 w-4 shrink-0 rounded border border-[var(--stroke-field)]"
        />
      )}
      <span className="truncate text-input-field text-[var(--text-primary)]">
        {name}
      </span>
      <button
        type="button"
        aria-label="Remove asset"
        onClick={onRemove}
        disabled={disabled}
        className="shrink-0 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        ×
      </button>
    </div>
  );
}

interface ParameterValueAreaProps {
  paramKey: string;
  displayName: string;
  assetIdKey: Path<FieldValues>;
  assetNameKey: Path<FieldValues>;
  assetUrlKey: Path<FieldValues>;
  registration: ReturnType<
    ReturnType<typeof useFormContext<FieldValues>>["register"]
  >;
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  onChangeRef: React.MutableRefObject<(() => void) | undefined>;
  textareaTextClass: string;
  disabled: boolean;
  onClearAsset: () => void;
  onClearError: () => void;
  onRemoveAsset: () => void;
}

function ParameterValueArea({
  paramKey,
  displayName,
  assetIdKey,
  assetNameKey,
  assetUrlKey,
  registration,
  textareaRef,
  onChangeRef,
  textareaTextClass,
  disabled,
  onClearAsset,
  onClearError,
  onRemoveAsset,
}: ParameterValueAreaProps) {
  const { control } = useFormContext<FieldValues>();
  const assetName = useWatch({ control, name: assetNameKey }) as
    | string
    | null
    | undefined;
  const value = useWatch({ control, name: paramKey as Path<FieldValues> }) as
    | string
    | undefined;

  if (assetName) {
    return (
      <AssetValueChip
        paramKey={paramKey}
        displayName={displayName}
        assetIdKey={assetIdKey}
        assetUrlKey={assetUrlKey}
        name={assetName}
        disabled={disabled}
        onRemove={onRemoveAsset}
      />
    );
  }

  const isColor = isColorParameter({ value: value ?? "", key: displayName });
  return (
    <div className="flex-1 min-w-0 relative flex items-center gap-2">
      {isColor && isHexColor(value ?? "") && (
        <ColorSwatch paramKey={paramKey} />
      )}
      <textarea
        id={`param-${paramKey}`}
        {...registration}
        ref={(e) => {
          textareaRef.current = e;
          registration.ref(e);
        }}
        onChange={(e) => {
          registration.onChange(e);
          // A hand-typed value no longer represents the picked asset.
          onClearAsset();
          // Typing dismisses a "can't be empty" error from a prior blank submit.
          onClearError();
          autoResize(e.target as HTMLTextAreaElement);
          onChangeRef.current?.();
        }}
        rows={1}
        className={`w-full bg-transparent border-none px-0 parameter-textarea text-input-field text-left ${textareaTextClass} placeholder-[var(--text-secondary)] focus:ring-0 focus:outline-none outline-none resize-none overflow-hidden`}
        style={{ lineHeight: "20px" }}
        disabled={disabled}
        placeholder="Value"
      />
    </div>
  );
}

interface ParameterRightSideProps {
  isAutoValue: boolean;
  isModified: boolean;
  disabled: boolean;
  isResyncing?: boolean;
  onTriggerClick: () => void;
}

function ParameterRightSide({
  isAutoValue,
  isModified,
  disabled,
  isResyncing,
  onTriggerClick,
}: ParameterRightSideProps) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <ParameterIndicators
        isAutoValue={isAutoValue}
        isModified={isModified}
        isResyncing={isResyncing}
      />
      <AssetTrigger disabled={disabled} onClick={onTriggerClick} />
    </div>
  );
}

/** Sibling form paths for a parameter's asset link (derived from the value path). */
function assetFieldKeys(paramKey: string) {
  const base = paramKey.replace(/\.value$/, "");
  return {
    assetIdKey: `${base}.asset_id` as Path<FieldValues>,
    assetNameKey: `${base}.asset_name` as Path<FieldValues>,
    assetUrlKey: `${base}.asset_url` as Path<FieldValues>,
  };
}

function useParameterInputState(
  paramKey: string,
  onChange: (() => void) | undefined,
  originalValue: string,
) {
  const { register, setValue, getValues, control, clearErrors, getFieldState } =
    useFormContext<FieldValues>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialHex, setModalInitialHex] = useState("");
  const { assetIdKey, assetNameKey, assetUrlKey } = assetFieldKeys(paramKey);

  // Scoped subscription so the field re-renders when its validation error (set
  // on submit when the user blanked a previously-set value) appears or clears.
  const formState = useFormState({
    control,
    name: paramKey as Path<FieldValues>,
  });
  const fieldError = getFieldState(
    paramKey as Path<FieldValues>,
    formState,
  ).error;
  const errorMessage =
    typeof fieldError?.message === "string" ? fieldError.message : undefined;

  const setField = (key: Path<FieldValues>, value: string | null) =>
    setValue(key, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

  // Write the value and the links to whatever it came from together, so the
  // three never disagree about which image or asset backs this parameter.
  const commitSelection = (selection: ParameterAssetSelection) => {
    setField(paramKey as Path<FieldValues>, selection.value);
    setField(assetIdKey, selection.assetId);
    setField(assetNameKey, selection.assetName);
    setField(assetUrlKey, selection.assetUrl);
    onChange?.();
  };

  // A hand-typed value no longer represents the asset it may have come from.
  // The value itself is left alone — the user is mid-edit.
  const clearAssetLink = () => {
    setField(assetIdKey, null);
    setField(assetNameKey, null);
    setField(assetUrlKey, null);
  };

  const openModal = () => {
    const value = getValues(paramKey as Path<FieldValues>) as
      | string
      | undefined;
    setModalInitialHex(value ?? "");
    setIsModalOpen(true);
  };
  return {
    registration: register(paramKey as Path<FieldValues>, {
      validate: (value) => validateNotBlanked(value, originalValue),
    }),
    isModalOpen,
    modalInitialHex,
    assetIdKey,
    assetNameKey,
    assetUrlKey,
    openModal,
    closeModal: () => setIsModalOpen(false),
    commitSelection,
    clearAssetLink,
    clearAsset: () => commitSelection(CLEARED_PARAMETER_ASSET),
    errorMessage,
    clearFieldError: () => clearErrors(paramKey as Path<FieldValues>),
  };
}

export function ParameterInput({
  paramKey,
  displayName,
  disabled = false,
  onChange,
  isModified = false,
  isAutoValue = false,
  groupName,
  projectId,
  isResyncing = false,
  originalValue = "",
}: ParameterInputProps) {
  const { onChangeRef, textareaRef } = useParameterInputRefs(onChange);
  const label = displayName || paramKey;
  const formattedLabel = useMemo(() => formatParameterLabel(label), [label]);
  // While the value is being re-read from the new render, the field is about
  // to be overwritten — render and behave as disabled so it reads as pending
  // rather than out of sync.
  const isFieldDisabled = disabled || isResyncing;
  const {
    registration,
    isModalOpen,
    modalInitialHex,
    assetIdKey,
    assetNameKey,
    assetUrlKey,
    openModal,
    closeModal,
    commitSelection,
    clearAssetLink,
    clearAsset,
    errorMessage,
    clearFieldError,
  } = useParameterInputState(paramKey, onChange, originalValue);
  const textareaTextClass =
    isAutoValue && !isModified
      ? "text-[var(--text-secondary)]"
      : "text-[var(--text-primary)]";
  return (
    <div className="flex flex-col gap-1">
      <div
        className={buildContainerClasses(
          isFieldDisabled,
          isModified,
          Boolean(errorMessage),
        )}
      >
        <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
          <label
            htmlFor={`param-${paramKey}`}
            className="text-label-field select-none shrink-0"
            style={{ lineHeight: "20px" }}
          >
            {formattedLabel}
          </label>
          <ParameterValueArea
            paramKey={paramKey}
            displayName={formattedLabel}
            assetIdKey={assetIdKey}
            assetNameKey={assetNameKey}
            assetUrlKey={assetUrlKey}
            registration={registration}
            textareaRef={textareaRef}
            onChangeRef={onChangeRef}
            textareaTextClass={textareaTextClass}
            disabled={isFieldDisabled}
            onClearAsset={clearAssetLink}
            onClearError={clearFieldError}
            onRemoveAsset={clearAsset}
          />
        </div>
        <ParameterRightSide
          isAutoValue={isAutoValue}
          isModified={isModified}
          disabled={isFieldDisabled}
          isResyncing={isResyncing}
          onTriggerClick={openModal}
        />
        <ParameterAssetModal
          isOpen={isModalOpen}
          initialHex={modalInitialHex}
          paramKey={label}
          groupName={groupName}
          projectId={projectId}
          onSelect={commitSelection}
          onClose={closeModal}
        />
      </div>
      {errorMessage && (
        <p
          role="alert"
          className="text-xs text-[var(--accent-destructive)] pl-1"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}
